/** Integrations tab of the tour-operator edit page.
 *
 * One grid over both booking-system accounts and vehicle-tracking systems,
 * fed by the combined `/integrations/` endpoint. Small, unpaginated row set →
 * a plain DataGridPro with custom action/toggle cells (not the server-paginated
 * ResourceDataGrid). Row click opens the edit drawer.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import type { IntegrationRow, WebhookLastReceived } from '@/api/integrations';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import {
  accountActions,
  activityActions,
  fleetSystemActions,
  importSourceActions,
  restechActions,
  type TenantCtx,
} from '../tenantLinks';

import { CATALOG_STALE_MS } from './cachePolicy';
import { IntegrationCreateDialog } from './IntegrationCreateDialog';
import { IntegrationEditDialog } from './IntegrationEditDialog';

const WEBHOOK_TYPES: (keyof WebhookLastReceived)[] = ['booking', 'order', 'departure', 'product'];

function fmtTs(ts: string | null | undefined): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'never' : d.toLocaleString();
}

/** Warns that an account is missing required config.
 *
 * Restechs that resolve credentials lazily construct fine without them and only
 * fail at the first API call, so without this the misconfiguration is invisible
 * here and only shows up as a failed sync or a rejected webhook.
 */
export function IncompleteConfigBadge({ row }: { row: IntegrationRow }) {
  if (row.config_status !== 'incomplete') return null;
  const keys = row.missing_keys ?? [];
  const title = keys.length
    ? `Not configured — missing ${keys.join(', ')}`
    : 'Not configured — required settings are missing';
  return (
    <Tooltip title={title}>
      <WarningAmberIcon
        color="warning"
        fontSize="small"
        aria-label={title}
        sx={{ flexShrink: 0 }}
      />
    </Tooltip>
  );
}

/** The freshness dot + local time both activity cells render. */
function ActivityDot({ latest, freshMs }: { latest: string | undefined; freshMs: number }) {
  const recent = latest ? Date.now() - new Date(latest).getTime() < freshMs : false;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: latest ? (recent ? 'success.main' : 'warning.main') : 'grey.400',
        }}
      />
      <Typography variant="caption">{latest ? fmtTs(latest) : 'never'}</Typography>
    </Box>
  );
}

function WebhookCell({ row, ctx }: { row: IntegrationRow; ctx: TenantCtx }) {
  if (!row.supports_webhooks) {
    return (
      <Typography variant="caption" color="text.disabled">
        n/a
      </Typography>
    );
  }
  const lr = row.webhook_last_received;
  const times = lr ? WEBHOOK_TYPES.map((t) => lr[t]).filter((v): v is string => !!v) : [];
  const latest = times.slice().sort().at(-1);
  const tip = lr ? WEBHOOK_TYPES.map((t) => `${t}: ${fmtTs(lr[t])}`).join('\n') : 'never';
  const dot = <ActivityDot latest={latest} freshMs={48 * 3600 * 1000} />;
  // "Last received" is only worth a popover once something HAS been received —
  // a ±1h window around "never" searches nothing.
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
      <Box component="span">
        {latest ? (
          <ValueActions actions={activityActions(latest, { integration: row.restech, t: ctx })}>
            {dot}
          </ValueActions>
        ) : (
          dot
        )}
      </Box>
    </Tooltip>
  );
}

function FleetActivityCell({ row, ctx }: { row: IntegrationRow; ctx: TenantCtx }) {
  const a = row.fleet_activity;
  if (!a) {
    return (
      <Typography variant="caption" color="text.disabled">
        n/a
      </Typography>
    );
  }
  const latest = a.last_pull_ts;
  const tip = [
    `Last pull: ${fmtTs(latest)}`,
    `Vehicles: ${a.vehicle_count}`,
    `Assigned to routes: ${a.vehicles_assigned}`,
  ].join('\n');
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
      <Box component="span">
        <ValueActions actions={fleetSystemActions(row.fleet_mgmt ?? 'fleet', ctx)}>
          {/* Fleet positions should be minutes-fresh (15min = the canonical
              "outdated" threshold). */}
          <ActivityDot latest={latest ?? undefined} freshMs={15 * 60 * 1000} />
        </ValueActions>
      </Box>
    </Tooltip>
  );
}

interface Props {
  to: TourOperatorDetail;
  onSaved: () => void;
  /** URL segments after ".../integrations": [integrationId, dialogTab?, view?]. */
  path: string[];
  /** Replace the URL segments after ".../integrations". */
  onNavigate: (sub: string[]) => void;
}

export function IntegrationsTab({ to, onSaved, path, onNavigate }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_restech_manage');

  const ctx: TenantCtx = { id: to.id, code: to.code };

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<IntegrationRow | null>(null);

  const rowsQuery = useQuery({
    queryKey: ['integrations', 'rows', to.id],
    queryFn: () => integrationsApi.listRows(to.id),
  });

  // Record dialog is URL-driven: .../integrations/<integrationId>[...]
  const editingId = path[0] ?? null;
  const editing =
    editingId !== null
      ? (rowsQuery.data?.results ?? []).find((r) => r.id === editingId) ?? null
      : null;
  const setEditing = (row: IntegrationRow | null) => onNavigate(row ? [row.id] : []);
  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: integrationsApi.listCatalog,
    staleTime: CATALOG_STALE_MS,
  });
  const nameById = new Map((catalogQuery.data?.results ?? []).map((e) => [e.id, e.name]));

  // Distinct Type/System options for the (client-side) select filter, drawn
  // from the loaded rows so the dropdown only lists systems actually present.
  const systemOptions = (() => {
    const seen = new Map<string, string>();
    for (const r of rowsQuery.data?.results ?? []) {
      const id = r.restech ?? r.fleet_mgmt ?? '';
      if (id && !seen.has(id)) seen.set(id, nameById.get(id) ?? id);
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  })();

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['integrations', 'rows', to.id] });
    onSaved();
  }

  const deleteMutation = useMutation({
    mutationFn: (r: IntegrationRow) => integrationsApi.remove(to.id, r.id),
    onSuccess: () => {
      snackbar.show('Integration deleted.', 'success');
      setPendingDelete(null);
      refresh();
    },
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ r, enabled }: { r: IntegrationRow; enabled: boolean }) =>
      integrationsApi.update(to.id, r.id, { enabled }),
    onSuccess: () => refresh(),
    onError: (e: unknown) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Update failed.', 'error'),
  });

  const columns: GridColDef<IntegrationRow>[] = [
    {
      field: 'kind',
      headerName: 'Kind',
      width: 110,
      type: 'singleSelect',
      valueOptions: [
        { value: 'booking', label: 'Restech' },
        { value: 'fleet', label: 'Fleet' },
      ],
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.row.kind === 'booking' ? 'Restech' : 'Fleet'}
          color={p.row.kind === 'booking' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'system',
      headerName: 'Type / System',
      width: 170,
      type: 'singleSelect',
      valueOptions: systemOptions,
      valueGetter: (_v, r) => r.restech ?? r.fleet_mgmt ?? '',
      renderCell: (p) => {
        const id = p.row.restech ?? p.row.fleet_mgmt ?? '';
        const actions = p.row.restech
          ? restechActions(p.row.restech, ctx)
          : p.row.fleet_mgmt
            ? fleetSystemActions(p.row.fleet_mgmt, ctx)
            : [];
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <ValueActions actions={actions}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {nameById.get(id) ?? id}
              </Box>
            </ValueActions>
            <IncompleteConfigBadge row={p.row} />
          </Stack>
        );
      },
    },
    {
      field: 'account',
      headerName: 'Account',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (p) => {
        if (p.row.kind === 'fleet') return <Typography variant="caption" color="text.disabled">—</Typography>;
        // The default account has no slug, so there is nothing to filter by.
        return p.row.is_default || !p.row.slug ? (
          <Chip size="small" variant="outlined" label="Default" />
        ) : (
          <ValueActions actions={accountActions(p.row.slug, ctx)}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {p.row.slug}
            </Box>
          </ValueActions>
        );
      },
    },
    {
      field: 'source_id',
      headerName: 'Source ID',
      flex: 1,
      minWidth: 280,
      renderCell: (p) =>
        p.row.source_id ? (
          <ValueActions actions={importSourceActions(p.row.source_id, ctx)}>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {p.row.source_id}
            </Box>
          </ValueActions>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 90,
      sortable: false,
      renderCell: (p) => {
        if (p.row.kind !== 'fleet') {
          // Booking (restech) accounts can't be disabled — show an always-on,
          // read-only switch (removal is deletion; webhooks toggle separately).
          return (
            <Tooltip title="Restech accounts are always enabled (delete to remove)">
              <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
                <Switch size="small" checked disabled />
              </Box>
            </Tooltip>
          );
        }
        return (
          <Switch
            size="small"
            checked={p.row.enabled}
            disabled={!canManage || toggleMutation.isPending}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => toggleMutation.mutate({ r: p.row, enabled: e.target.checked })}
          />
        );
      },
    },
    {
      field: 'activity',
      headerName: 'Activity',
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.kind === 'fleet' ? (
          <FleetActivityCell row={p.row} ctx={ctx} />
        ) : (
          <WebhookCell row={p.row} ctx={ctx} />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 96,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (p) => (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(p.row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {canManage && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDelete(p.row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  return (
    <PermissionGate
      perm="console_restech_view"
      fallback={<Alert severity="warning">You do not have permission to view integrations.</Alert>}
    >
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Integrations
          </Typography>
          <PermissionGate perm="console_restech_manage">
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
              Add integration
            </Button>
          </PermissionGate>
        </Box>

        {rowsQuery.error ? (
          <Alert severity="error">Couldn’t load integrations.</Alert>
        ) : (
          <DataGridPro
            autoHeight
            rows={rowsQuery.data?.results ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            loading={rowsQuery.isLoading}
            disableRowSelectionOnClick
            onRowClick={(p) => setEditing(p.row as IntegrationRow)}
            initialState={{ sorting: { sortModel: [{ field: 'kind', sort: 'asc' }] } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )}
      </Stack>

      <IntegrationCreateDialog
        open={createOpen}
        toId={to.id}
        toCode={to.code}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <IntegrationEditDialog
        open={editing !== null}
        toId={to.id}
        toCode={to.code}
        row={editing}
        canManage={canManage}
        onClose={() => setEditing(null)}
        onSaved={refresh}
        path={path.slice(1)}
        onNavigate={(sub) => onNavigate(editing ? [editing.id, ...sub] : sub)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete integration?"
        message={
          pendingDelete?.is_default
            ? `This clears the default configuration for ${pendingDelete?.restech ?? 'this system'}.`
            : pendingDelete?.kind === 'fleet'
              ? 'This permanently removes the fleet tracking configuration.'
              : `This removes the "${pendingDelete?.slug}" account. Its externally-configured webhook URLs will stop working.`
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />
    </PermissionGate>
  );
}
